import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Trash2, Save, Landmark } from 'lucide-react';
import { Button } from '../../ui/button';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const CATEGORIES = ['property', 'vehicle', 'equipment', 'cash', 'other'];

export default function NetWorthTab({ dealId, onRefresh }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState(null);

  const load = async () => {
    try {
      const res = await axios.get(`${API}/crm/deals/${dealId}/net-worth`);
      setItems(res.data || []);
    } catch {
      toast.error('Failed to load net worth');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [dealId]);

  const addItem = async () => {
    try {
      await axios.post(`${API}/crm/deals/${dealId}/net-worth`, { category: 'other' });
      await load();
      onRefresh?.();
      toast.success('Net worth item added');
    } catch {
      toast.error('Failed to add item');
    }
  };

  const updateItem = (id, key, value) => {
    setItems(prev => prev.map(item => item.id === id ? { ...item, [key]: value } : item));
  };

  const saveItem = async (item) => {
    setSavingId(item.id);
    try {
      await axios.put(`${API}/crm/net-worth/${item.id}`, {
        category: item.category || 'other',
        description: item.description || null,
        estimated_value: item.estimated_value === '' ? null : Number(item.estimated_value),
        debt_outstanding: item.debt_outstanding === '' ? null : Number(item.debt_outstanding),
        notes: item.notes || null,
      });
      onRefresh?.();
      toast.success('Net worth item saved');
    } catch {
      toast.error('Failed to save item');
    } finally {
      setSavingId(null);
    }
  };

  const deleteItem = async (id) => {
    if (!window.confirm('Delete this net worth item?')) return;
    try {
      await axios.delete(`${API}/crm/net-worth/${id}`);
      await load();
      onRefresh?.();
      toast.success('Net worth item deleted');
    } catch {
      toast.error('Failed to delete item');
    }
  };

  if (loading) return <div className="text-gray-400 text-sm" data-testid="net-worth-loading">Loading net worth...</div>;

  return (
    <div className="max-w-5xl space-y-4" data-testid="net-worth-tab">
      <div className="flex items-center justify-between">
        <h2 className="text-white font-medium flex items-center gap-2" data-testid="net-worth-title">
          <Landmark className="h-5 w-5 text-cyan-300" /> Net Worth
        </h2>
        <Button onClick={addItem} size="sm" className="bg-purple-600 hover:bg-purple-700 text-white" data-testid="add-net-worth-item-button">
          <Plus className="h-4 w-4 mr-1" /> Add Item
        </Button>
      </div>

      {items.length === 0 && (
        <div className="text-sm text-gray-500 text-center py-8 border border-white/10 rounded-xl" data-testid="net-worth-empty-state">
          No net worth items captured yet.
        </div>
      )}

      {items.map((item) => (
        <div key={item.id} className="bg-navy-800/50 border border-white/10 rounded-xl p-4 space-y-3" data-testid={`net-worth-row-${item.id}`}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Field label="Category">
              <select
                value={item.category || 'other'}
                onChange={(e) => updateItem(item.id, 'category', e.target.value)}
                className="w-full bg-navy-900 border border-white/10 rounded-lg px-3 py-2 text-sm text-white"
                data-testid={`net-worth-category-${item.id}`}
              >
                {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </Field>
            <Field label="Description">
              <input
                value={item.description || ''}
                onChange={(e) => updateItem(item.id, 'description', e.target.value)}
                className="w-full bg-navy-900 border border-white/10 rounded-lg px-3 py-2 text-sm text-white"
                data-testid={`net-worth-description-${item.id}`}
              />
            </Field>
            <Field label="Estimated Value ($)">
              <input
                type="number"
                value={item.estimated_value ?? ''}
                onChange={(e) => updateItem(item.id, 'estimated_value', e.target.value)}
                className="w-full bg-navy-900 border border-white/10 rounded-lg px-3 py-2 text-sm text-white"
                data-testid={`net-worth-estimated-value-${item.id}`}
              />
            </Field>
            <Field label="Debt Outstanding ($)">
              <input
                type="number"
                value={item.debt_outstanding ?? ''}
                onChange={(e) => updateItem(item.id, 'debt_outstanding', e.target.value)}
                className="w-full bg-navy-900 border border-white/10 rounded-lg px-3 py-2 text-sm text-white"
                data-testid={`net-worth-debt-outstanding-${item.id}`}
              />
            </Field>
            <Field label="Notes" className="md:col-span-2">
              <textarea
                rows={2}
                value={item.notes || ''}
                onChange={(e) => updateItem(item.id, 'notes', e.target.value)}
                className="w-full bg-navy-900 border border-white/10 rounded-lg px-3 py-2 text-sm text-white"
                data-testid={`net-worth-notes-${item.id}`}
              />
            </Field>
          </div>
          <div className="flex justify-between">
            <Button onClick={() => deleteItem(item.id)} variant="ghost" className="text-red-400 hover:bg-red-500/10" data-testid={`delete-net-worth-item-${item.id}`}>
              <Trash2 className="h-4 w-4 mr-1" /> Delete
            </Button>
            <Button onClick={() => saveItem(item)} disabled={savingId === item.id} className="bg-purple-600 hover:bg-purple-700" data-testid={`save-net-worth-item-${item.id}`}>
              <Save className="h-4 w-4 mr-1" /> {savingId === item.id ? 'Saving...' : 'Save'}
            </Button>
          </div>
        </div>
      ))}
    </div>
  );
}

function Field({ label, className = '', children }) {
  return (
    <div className={className}>
      <label className="block text-xs text-gray-400 mb-1">{label}</label>
      {children}
    </div>
  );
}
